import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { FiMic, FiMicOff, FiMessageSquare, FiX } from 'react-icons/fi';

import { closeAiLiveSession, createAiLiveSession } from '../services/aiService';

const OUTPUT_SAMPLE_RATE = 24000;
const TARGET_INPUT_SAMPLE_RATE = 16000;
const MAX_RETRIES = 3;

// --- Audio utility functions (ported from mobile live-webview.ts) ---

function base64FromBytes(bytes) {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, Array.from(chunk));
  }
  return btoa(binary);
}

function bytesFromBase64(base64Value) {
  const binary = atob(base64Value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function downsample(buffer, inputSampleRate, outputSampleRate) {
  if (outputSampleRate >= inputSampleRate) return buffer;
  const ratio = inputSampleRate / outputSampleRate;
  const nextLength = Math.round(buffer.length / ratio);
  const result = new Float32Array(nextLength);
  let offsetResult = 0;
  let offsetBuffer = 0;
  while (offsetResult < result.length) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
    let accum = 0;
    let count = 0;
    for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i += 1) {
      accum += buffer[i];
      count += 1;
    }
    result[offsetResult] = count ? accum / count : 0;
    offsetResult += 1;
    offsetBuffer = nextOffsetBuffer;
  }
  return result;
}

function convertFloatTo16BitPcm(floatSamples) {
  const pcm = new Int16Array(floatSamples.length);
  for (let i = 0; i < floatSamples.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, floatSamples[i]));
    pcm[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return new Uint8Array(pcm.buffer);
}

// --- Status indicator component ---

function StatusIndicator({ status }) {
  if (status === 'connecting') {
    return (
      <HStack spacing={2}>
        <Spinner size="sm" color="blue.400" />
        <Text fontSize="sm" color="gray.500">Connecting...</Text>
      </HStack>
    );
  }
  if (status === 'listening') {
    return (
      <HStack spacing={2}>
        <Box w="10px" h="10px" borderRadius="full" bg="green.400" sx={{
          animation: 'pulse 1.5s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 1, transform: 'scale(1)' },
            '50%': { opacity: 0.5, transform: 'scale(1.3)' },
          },
        }} />
        <Text fontSize="sm" color="gray.600">Listening...</Text>
      </HStack>
    );
  }
  if (status === 'speaking') {
    return (
      <HStack spacing={2} align="center">
        {[0, 1, 2, 3].map((i) => (
          <Box
            key={i}
            w="3px"
            bg="blue.400"
            borderRadius="full"
            sx={{
              animation: `speakBar 0.8s ease-in-out ${i * 0.15}s infinite`,
              '@keyframes speakBar': {
                '0%, 100%': { height: '8px' },
                '50%': { height: '20px' },
              },
            }}
          />
        ))}
        <Text fontSize="sm" color="gray.600" ml={1}>Speaking...</Text>
      </HStack>
    );
  }
  if (status === 'thinking') {
    return (
      <HStack spacing={2}>
        <Spinner size="xs" color="blue.400" />
        <Text fontSize="sm" color="gray.500">Thinking...</Text>
      </HStack>
    );
  }
  if (status === 'error') {
    return <Text fontSize="sm" color="red.500">Connection error</Text>;
  }
  if (status === 'ended') {
    return <Text fontSize="sm" color="gray.500">Session ended</Text>;
  }
  return null;
}

// --- Main component ---

export default function AiVoiceModal({
  isOpen,
  onClose,
  role,
  sessionId,
  screenContext,
  onFallbackToText,
}) {
  const [status, setStatus] = useState('idle');
  const [transcriptEntries, setTranscriptEntries] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  // Refs for mutable state that should not trigger re-renders
  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const processorNodeRef = useRef(null);
  const muteNodeRef = useRef(null);
  const playbackNodesRef = useRef([]);
  const nextPlaybackTimeRef = useRef(0);
  const liveSessionIdRef = useRef(null);
  const retriesRef = useRef(0);
  const closedByClientRef = useRef(false);
  const userTurnIdRef = useRef(1);
  const assistantTurnIdRef = useRef(1);
  const currentUserTranscriptRef = useRef('');
  const currentAssistantTranscriptRef = useRef('');
  const scrollBottomRef = useRef(null);
  const mountedRef = useRef(true);

  // Auto-scroll to bottom when transcripts update
  useEffect(() => {
    scrollBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcriptEntries]);

  // --- Cleanup helpers ---

  const clearPlayback = useCallback(() => {
    playbackNodesRef.current.forEach((node) => {
      try { node.stop(0); } catch (_e) { /* ignore */ }
    });
    playbackNodesRef.current = [];
    nextPlaybackTimeRef.current = audioCtxRef.current ? audioCtxRef.current.currentTime : 0;
  }, []);

  const stopMicrophone = useCallback(() => {
    if (processorNodeRef.current) {
      processorNodeRef.current.disconnect();
      processorNodeRef.current.onaudioprocess = null;
      processorNodeRef.current = null;
    }
    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }
    if (muteNodeRef.current) {
      muteNodeRef.current.disconnect();
      muteNodeRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  const closeSessionCleanly = useCallback(async () => {
    closedByClientRef.current = true;
    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ realtimeInput: { audioStreamEnd: true } }));
      }
    } catch (_e) { /* ignore */ }
    stopMicrophone();
    clearPlayback();
    if (wsRef.current) {
      try { wsRef.current.close(1000, 'client_closed'); } catch (_e) { /* ignore */ }
    }
    wsRef.current = null;
    if (mountedRef.current) setStatus('ended');
    // Notify backend that the live session ended
    if (liveSessionIdRef.current) {
      try {
        await closeAiLiveSession(liveSessionIdRef.current);
      } catch (_e) { /* ignore */ }
      liveSessionIdRef.current = null;
    }
  }, [stopMicrophone, clearPlayback]);

  // --- Transcript management ---

  const emitTranscript = useCallback((entryRole, text, stage) => {
    const normalized = String(text || '').trim();
    if (!normalized) return;
    const id = entryRole === 'assistant'
      ? `assistant:${assistantTurnIdRef.current}`
      : `user:${userTurnIdRef.current}`;
    setTranscriptEntries((prev) => {
      const existing = prev.findIndex((e) => e.id === id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { ...updated[existing], text: normalized, stage };
        return updated;
      }
      return [...prev, { id, role: entryRole, text: normalized, stage }];
    });
  }, []);

  const finalizeTurn = useCallback(() => {
    if (currentUserTranscriptRef.current) {
      emitTranscript('user', currentUserTranscriptRef.current, 'final');
    }
    if (currentAssistantTranscriptRef.current) {
      emitTranscript('assistant', currentAssistantTranscriptRef.current, 'final');
    }
    if (currentUserTranscriptRef.current) userTurnIdRef.current += 1;
    if (currentAssistantTranscriptRef.current || currentUserTranscriptRef.current) {
      assistantTurnIdRef.current += 1;
    }
    currentUserTranscriptRef.current = '';
    currentAssistantTranscriptRef.current = '';
  }, [emitTranscript]);

  // --- Audio playback ---

  const playAudioChunk = useCallback((base64Value) => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const bytes = bytesFromBase64(base64Value);
    const pcm = new Int16Array(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength));
    const buffer = ctx.createBuffer(1, pcm.length, OUTPUT_SAMPLE_RATE);
    const channelData = buffer.getChannelData(0);
    for (let i = 0; i < pcm.length; i += 1) {
      channelData[i] = pcm[i] / 0x8000;
    }
    try {
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      nextPlaybackTimeRef.current = Math.max(ctx.currentTime + 0.05, nextPlaybackTimeRef.current);
      source.start(nextPlaybackTimeRef.current);
      nextPlaybackTimeRef.current += buffer.duration;
      playbackNodesRef.current.push(source);
      source.onended = () => {
        playbackNodesRef.current = playbackNodesRef.current.filter((n) => n !== source);
        if (!playbackNodesRef.current.length && mountedRef.current) {
          setStatus('listening');
        }
      };
      if (mountedRef.current) setStatus('speaking');
    } catch (_e) { /* playback error */ }
  }, []);

  // --- Send audio to WebSocket ---

  const sendRealtimeAudio = useCallback((floatSamples) => {
    const ws = wsRef.current;
    const ctx = audioCtxRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !ctx) return;
    const downsampled = downsample(floatSamples, ctx.sampleRate, TARGET_INPUT_SAMPLE_RATE);
    if (!downsampled.length) return;
    const bytes = convertFloatTo16BitPcm(downsampled);
    ws.send(JSON.stringify({
      realtimeInput: {
        audio: {
          data: base64FromBytes(bytes),
          mimeType: `audio/pcm;rate=${TARGET_INPUT_SAMPLE_RATE}`,
        },
      },
    }));
  }, []);

  // --- Microphone setup ---

  const startMicrophone = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('Live voice requires browser microphone support.');
    }
    mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
      audio: { autoGainControl: true, channelCount: 1, echoCancellation: true, noiseSuppression: true },
    });
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioCtx();
    }
    if (audioCtxRef.current.state === 'suspended') {
      await audioCtxRef.current.resume();
    }
    sourceNodeRef.current = audioCtxRef.current.createMediaStreamSource(mediaStreamRef.current);
    // NOTE: createScriptProcessor is deprecated in favor of AudioWorkletNode.
    // Kept for now because AudioWorklet requires a separate .js module loaded via addModule(),
    // adding build complexity. Browser support for ScriptProcessor remains universal.
    // TODO: Migrate to AudioWorkletNode when build pipeline supports worklet files.
    processorNodeRef.current = audioCtxRef.current.createScriptProcessor(4096, 1, 1);
    muteNodeRef.current = audioCtxRef.current.createGain();
    muteNodeRef.current.gain.value = 0;
    processorNodeRef.current.onaudioprocess = (event) => {
      sendRealtimeAudio(event.inputBuffer.getChannelData(0));
    };
    sourceNodeRef.current.connect(processorNodeRef.current);
    processorNodeRef.current.connect(muteNodeRef.current);
    muteNodeRef.current.connect(audioCtxRef.current.destination);
    if (mountedRef.current) setStatus('listening');
  }, [sendRealtimeAudio]);

  // --- Handle incoming server messages ---

  const handleServerContent = useCallback((content) => {
    if (!content) return;
    if (content.interrupted) {
      clearPlayback();
      currentAssistantTranscriptRef.current = '';
      if (mountedRef.current) setStatus('listening');
    }
    if (content.inputTranscription && content.inputTranscription.text) {
      currentUserTranscriptRef.current = content.inputTranscription.text;
      emitTranscript('user', currentUserTranscriptRef.current, 'partial');
    }
    if (content.outputTranscription && content.outputTranscription.text) {
      currentAssistantTranscriptRef.current = content.outputTranscription.text;
      emitTranscript('assistant', currentAssistantTranscriptRef.current, 'partial');
      if (mountedRef.current) setStatus('speaking');
    }
    if (content.generationComplete) {
      if (mountedRef.current) {
        setStatus(currentAssistantTranscriptRef.current ? 'speaking' : 'thinking');
      }
    }
    if (content.turnComplete && !playbackNodesRef.current.length) {
      finalizeTurn();
      if (mountedRef.current) setStatus('listening');
    }
    const parts = (content.modelTurn?.parts) || [];
    parts.forEach((part) => {
      if (part && part.inlineData && part.inlineData.data) {
        playAudioChunk(part.inlineData.data);
      }
    });
  }, [clearPlayback, emitTranscript, finalizeTurn, playAudioChunk]);

  // --- Main connection flow ---

  const connect = useCallback(async () => {
    closedByClientRef.current = false;
    if (mountedRef.current) {
      setStatus('connecting');
      setErrorMessage('');
    }

    // 1. Create live session via backend
    let liveData;
    try {
      liveData = await createAiLiveSession({
        sessionId,
        currentScreen: screenContext,
        locale: (() => { try { return window.localStorage.getItem('logiflow.language'); } catch (_) { return null; } })() || 'en',
      });
    } catch (err) {
      if (mountedRef.current) {
        setStatus('error');
        setErrorMessage(err?.message || 'Failed to create live session.');
      }
      return;
    }

    const { sessionId: liveId, liveSession } = liveData;
    liveSessionIdRef.current = liveId;
    const { token, model, apiVersion, liveConfig } = liveSession;
    if (!token?.name) {
      if (mountedRef.current) {
        setStatus('error');
        setErrorMessage('Live voice token missing.');
      }
      return;
    }

    // 2. Open WebSocket to Gemini Live
    // NOTE: Gemini Live WS API requires the ephemeral token as a query parameter.
    // The token is short-lived and scoped to this single session — it cannot be reused.
    // Ensure error-reporting tools (Sentry, etc.) filter URLs matching this pattern.
    const wsUrl =
      `wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.${apiVersion}.GenerativeService.BidiGenerateContent?access_token=${encodeURIComponent(token.name)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      const cfg = liveConfig || {};
      ws.send(JSON.stringify({
        setup: {
          model: model.startsWith('models/') ? model : `models/${model}`,
          generationConfig: { responseModalities: cfg.responseModalities || ['AUDIO'] },
          systemInstruction: cfg.systemInstruction
            ? { parts: [{ text: cfg.systemInstruction }] }
            : undefined,
          inputAudioTranscription: cfg.inputAudioTranscription || undefined,
          outputAudioTranscription: cfg.outputAudioTranscription || undefined,
        },
      }));
    };

    ws.onmessage = async (event) => {
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch (_e) {
        return;
      }
      if (payload.setupComplete) {
        try {
          await startMicrophone();
        } catch (err) {
          const denied = err && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError');
          if (mountedRef.current) {
            setErrorMessage(denied ? 'Microphone permission denied.' : (err.message || 'Unable to access the microphone.'));
            setStatus('error');
          }
          await closeSessionCleanly();
        }
        return;
      }
      if (payload.serverContent) {
        handleServerContent(payload.serverContent);
      }
      if (payload.goAway) {
        finalizeTurn();
        if (mountedRef.current) setStatus('ended');
      }
    };

    ws.onerror = () => {
      if (mountedRef.current) {
        setErrorMessage('The live voice connection failed.');
        setStatus('error');
      }
    };

    ws.onclose = (event) => {
      stopMicrophone();
      clearPlayback();
      if (!closedByClientRef.current && mountedRef.current) {
        // Unexpected close -- attempt retry
        if (retriesRef.current < MAX_RETRIES) {
          retriesRef.current += 1;
          setStatus('connecting');
          setErrorMessage(`Connection lost. Reconnecting (attempt ${retriesRef.current}/${MAX_RETRIES})...`);
          // Small delay before retry
          setTimeout(() => {
            if (mountedRef.current) connect();
          }, 1000);
        } else {
          setStatus('ended');
          setErrorMessage(
            event.reason
              ? `Session ended: ${event.reason}`
              : 'Voice session ended unexpectedly.'
          );
        }
      }
    };
  }, [
    sessionId,
    screenContext,
    startMicrophone,
    handleServerContent,
    finalizeTurn,
    closeSessionCleanly,
    stopMicrophone,
    clearPlayback,
  ]);

  // --- Lifecycle: connect on open, cleanup on close ---

  useEffect(() => {
    mountedRef.current = true;
    if (isOpen) {
      retriesRef.current = 0;
      userTurnIdRef.current = 1;
      assistantTurnIdRef.current = 1;
      currentUserTranscriptRef.current = '';
      currentAssistantTranscriptRef.current = '';
      setTranscriptEntries([]);
      setErrorMessage('');
      setStatus('idle');
      connect();
    }
    return () => {
      mountedRef.current = false;
      // Full cleanup when modal closes or unmounts
      closedByClientRef.current = true;
      stopMicrophone();
      clearPlayback();
      if (wsRef.current) {
        try {
          if (wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ realtimeInput: { audioStreamEnd: true } }));
          }
          wsRef.current.close(1000, 'client_closed');
        } catch (_e) { /* ignore */ }
        wsRef.current = null;
      }
      if (audioCtxRef.current) {
        try { audioCtxRef.current.close(); } catch (_e) { /* ignore */ }
        audioCtxRef.current = null;
      }
      if (liveSessionIdRef.current) {
        closeAiLiveSession(liveSessionIdRef.current).catch(() => {});
        liveSessionIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // --- Event handlers ---

  const handleClose = useCallback(() => {
    closeSessionCleanly();
    onClose();
  }, [closeSessionCleanly, onClose]);

  const handleFallbackToText = useCallback(() => {
    const draft = transcriptEntries
      .filter((e) => e.role === 'user' && e.stage === 'final')
      .map((e) => e.text)
      .join('\n');
    closeSessionCleanly();
    if (onFallbackToText) onFallbackToText(draft || '');
  }, [transcriptEntries, closeSessionCleanly, onFallbackToText]);

  const isActive = status === 'listening' || status === 'speaking' || status === 'thinking';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent maxH="80vh" display="flex" flexDirection="column">
        <ModalCloseButton />
        <ModalHeader borderBottomWidth="1px">
          <HStack spacing={3} align="center">
            <Box
              w="36px"
              h="36px"
              borderRadius="full"
              bg={isActive ? 'blue.500' : 'gray.300'}
              display="flex"
              alignItems="center"
              justifyContent="center"
              transition="background 0.3s"
            >
              {isActive
                ? <FiMic color="white" size={18} />
                : <FiMicOff color="white" size={18} />
              }
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="700" color="navy.700">
                Voice assistant
              </Text>
              <StatusIndicator status={status} />
            </Box>
          </HStack>
        </ModalHeader>

        <ModalBody flex={1} overflowY="auto" py={4}>
          {errorMessage ? (
            <Box
              borderWidth="1px"
              borderRadius="2xl"
              borderColor="orange.200"
              bg="orange.50"
              px={4}
              py={3}
              mb={4}
            >
              <Text fontSize="sm" color="gray.700">{errorMessage}</Text>
            </Box>
          ) : null}

          {!transcriptEntries.length && status !== 'error' && status !== 'ended' ? (
            <Box
              borderWidth="1px"
              borderRadius="2xl"
              borderColor="gray.200"
              bg="gray.50"
              px={4}
              py={4}
              textAlign="center"
            >
              <Text fontWeight="700" color="navy.700">
                {status === 'connecting' ? 'Connecting to voice assistant...' : 'Speak naturally'}
              </Text>
              <Text mt={1} fontSize="sm" color="gray.600">
                The assistant will listen and respond with voice. Your conversation will appear here as text.
              </Text>
            </Box>
          ) : null}

          <VStack spacing={2} align="stretch">
            {transcriptEntries.map((entry) => (
              <HStack
                key={entry.id}
                justifyContent={entry.role === 'user' ? 'flex-end' : 'flex-start'}
              >
                <Box
                  bg={entry.role === 'user' ? 'blue.500' : 'gray.50'}
                  color={entry.role === 'user' ? 'white' : 'navy.700'}
                  borderWidth={entry.role === 'user' ? '0' : '1px'}
                  borderColor="gray.200"
                  px={3}
                  py={2}
                  borderRadius="lg"
                  maxW="80%"
                  opacity={entry.stage === 'partial' ? 0.7 : 1}
                >
                  <Text fontSize="sm" lineHeight="1.6">
                    {entry.text}
                  </Text>
                </Box>
              </HStack>
            ))}
            <div ref={scrollBottomRef} />
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" justifyContent="center">
          <HStack spacing={4}>
            <Tooltip label="End voice session" placement="top">
              <IconButton
                aria-label="End voice session"
                icon={<FiX />}
                onClick={handleClose}
                colorScheme="red"
                variant="outline"
                borderRadius="full"
                size="lg"
              />
            </Tooltip>
            {onFallbackToText ? (
              <Tooltip label="Switch to text" placement="top">
                <IconButton
                  aria-label="Switch to text assistant"
                  icon={<FiMessageSquare />}
                  onClick={handleFallbackToText}
                  colorScheme="blue"
                  variant="outline"
                  borderRadius="full"
                  size="lg"
                />
              </Tooltip>
            ) : null}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
